import React, { memo, useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Backdrop,
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Menu,
  MenuItem,
  CircularProgress,
  Stepper,
  StepLabel,
  Step,
  Autocomplete,
  Breadcrumbs,
  Checkbox,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as MoreVertIcon } from '../../../../assets/common-assets/moreVertical.svg';
import closeIcon from '../../../../assets/accountSetting-assets/x-circle.svg';
import { lightPalette } from '../../../../theme';
import { FilterPopup, QontoConnector, Root, classes } from './utils';
import { FILTER, INVOICE_FLOW } from '../../../../constants/staticContent';
import {
  deleteFlow,
  editInvoiceConfigurationFlow,
  getAllFlowData,
} from '../../../../services/flowConfiguration';
import { logInfo } from '../../../../services/flowLogs';
import Toast from '../../utils/Toast';
import {
  createToken,
  googleUploadFlow,
  microSoftUploadFlow,
} from '../../../../services/uploadApi';
import { checkDocutranscribeNode, CustomStepIcon } from './nodeConnection';
import CustomTooltip from '../../utils/CustomTooltip';
import { globalClasses } from '../../utils/globalUtils';
import { connectSocket } from '../../../../store/socketConfigSlice';
import { completeNotification } from '../../../../store/socketSlice';
import SelectOption from './SelectOption';
import AddFlow from './AddFlow';

const edgeMessage = 'Node is not connected,please connect to node';
const FlowConfiguration = () => {
  const dispatch = useDispatch();
  const isConnected = useSelector((states) => states.socketConfig.isConnected);
  const notifyUser = useSelector((states) => states.socket.notification);
  const notifyUploadedExported = notifyUser?.isUploadedExported;
  const notificationMessage = notifyUser?.message;
  const DocumentCompletedData = notifyUser?.status;
  const userDetail = useSelector((states) => states.user.user);
  const userId = userDetail?.id;
  const isConnectedRef = useRef(isConnected);
  isConnectedRef.current = isConnected;
  const [editNewFlow, setEditNewFlow] = useState(false);
  const [deleteConfigurationFlow, setDeleteConfigurationFlow] = useState(false);
  const [flows, setFlows] = useState([]);
  const [currentFlow, setCurrentFlow] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [statusLoader, setStatusLoader] = useState({ stage: false, id: '' });
  const [isBackDropLoader, setIsBackDropLoader] = useState(false);
  const navigate = useNavigate();
  const [connectedNodes, setConnectedNodes] = useState([]);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filterAnchorElField, setFilterAnchorElField] = useState('');
  const [filterValue, setFilterValue] = useState({ name: '', status: '' });
  const [applyFilter, setApplyFilter] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  const statusOption = ['Stopped', 'Running'];
  useEffect(() => {
    if (
      notificationMessage &&
      (DocumentCompletedData === 'Completed' || notifyUploadedExported)
    ) {
      Toast({ message: `${notificationMessage}`, variant: 'success' });

      dispatch(completeNotification({}));
    }
  }, [notificationMessage]);

  const filterOpen = Boolean(filterAnchorEl);
  const handleFilterClick = (event, field) => {
    setFilterAnchorEl(event.currentTarget);
    setFilterAnchorElField(field);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
    setApplyFilter(false);
  };

  const handleFilterChange = (event) => {
    setFilterValue((prev) => ({
      ...prev,
      name: event.target.value,
    }));
    setApplyFilter(false);
  };

  const handleAutocompleteChange = (event, newValue) => {
    setFilterValue((prev) => ({
      ...prev,
      status: newValue,
    }));
    setApplyFilter(false);
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event, selectedFlow) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setCurrentFlow(selectedFlow);
  };
  const handleClose = (event) => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  // thumbnail Sequence
  const orderedConnections = (allEdges, extractBaseName) => {
    const connections = allEdges?.map((edge) => ({
      source: edge.source,
      target: edge.target,
      sourceBase: extractBaseName(edge.source),
      targetBase: extractBaseName(edge.target),
    }));

    const map = {};
    connections?.forEach((connection) => {
      map[connection.source] = connection.target;
    });
    // Find the start node
    const startNode = connections?.find(
      (connection) => !Object.values(map).includes(connection?.source),
    )?.source;
    // Recursive function to build the ordered sequence
    const buildSequence = (node, sequence = []) => {
      if (node) {
        sequence.push(node);
        return buildSequence(map[node], sequence);
      }
      return sequence;
    };

    const sequence = buildSequence(startNode);
    return sequence?.map(extractBaseName);
  };

  const getAllFlow = async (filter) => {
    const res = await getAllFlowData(filter);
    if (res) {
      res?.forEach((flow) => {
        const edges = flow?.details?.edge;
        const extractBaseName = (str) => str.split('_')[0];

        const result = orderedConnections(edges, extractBaseName);
        setConnectedNodes((prev) => ({
          ...prev,
          [flow?.id]: result,
        }));
      });
      setFlows(res);
    }
    setIsLoading(false);
  };

  const openEditBackdrop = (event) => {
    event.stopPropagation();
    setAnchorEl(null);
    setEditNewFlow(true);
  };

  const openAddFlowBackdrop = () => {
    setAnchorEl(null);
    setEditNewFlow(true);
  };

  const openDeleteBackdrop = (event) => {
    event.stopPropagation();
    setAnchorEl(null);
    setDeleteConfigurationFlow(true);
  };

  const openLogs = (event, flow) => {
    event.stopPropagation();
    navigate(`/log-dashboard/${flow?.id}`);
  };

  const closeKeyNameForm = () => {
    setEditNewFlow(false);
    setDeleteConfigurationFlow(false);
    setCurrentFlow(null);
  };

  const handleDelete = async (id) => {
    setIsBackDropLoader(true);
    const responseStatus = await deleteFlow(id);
    if (responseStatus === 200) {
      getAllFlow(filterValue);
      setCurrentFlow(null);
      setSelectedRows((prev) => prev.filter((ele) => ele !== id));
      setDeleteConfigurationFlow(false);
    }
    setIsBackDropLoader(false);
  };

  const handleEditorClick = (flowId) => {
    navigate(`/flow-configuration/editor?flowId=${flowId}`);
  };

  useEffect(() => {
    getAllFlow(filterValue);
  }, []);

  const validateAccount = (account, fields) => {
    const missingFields = fields.filter((field) => !account?.[field]);
    return { isValid: missingFields.length === 0, missingFields };
  };

  const showMissingFieldError = (serviceName, missingFields) => {
    if (missingFields.length > 0) {
      const field = missingFields[0];
      const errorMessage =
        field === 'email'
          ? `Email is required for ${serviceName}`
          : `${field.charAt(0).toUpperCase() + field.slice(1)} selection is required for ${serviceName}`;
      Toast({ message: errorMessage, variant: 'warning' });
      return true; // Indicate that an error was shown
    }
    return false; // No error was shown
  };

  const isNodeConnected = (nodes, edges) => {
    const isOneDrive = checkDocutranscribeNode(nodes, edges, 'OneDrive');
    const isSharePoint = checkDocutranscribeNode(nodes, edges, 'SharePoint');
    const isOutlook = checkDocutranscribeNode(nodes, edges, 'Outlook');
    const isGmail = checkDocutranscribeNode(nodes, edges, 'Gmail');
    const isGoogleDrive = checkDocutranscribeNode(nodes, edges, 'GoogleDrive');

    return {
      isOneDrive,
      isSharePoint,
      isOutlook,
      isGmail,
      isGoogleDrive,
    };
  };

  const handleFlowRunning = async (event, flow) => {
    event.stopPropagation();

    if (!flow || !flow.details) {
      Toast({
        message: `${INVOICE_FLOW.FLOW_CONFIGURATION}`,
        variant: 'warning',
      });
      return;
    }

    const { node, edge, selectedAccount, actionsConfigure } = flow.details;

    const { isOneDrive, isSharePoint, isOutlook, isGmail, isGoogleDrive } =
      isNodeConnected(node, edge);

    const nodeLabels = new Set(node.map((singleNode) => singleNode.data.label));
    const hasDocutranscribe = nodeLabels.has('Docutranscribe');

    const oneDriveValidation = validateAccount(selectedAccount?.OneDrive, [
      'email',
      'token',
      'driveId',
      'folderId',
    ]);
    const sharePointValidation = validateAccount(selectedAccount?.SharePoint, [
      'email',
      'token',
      'driveId',
      'folderId',
    ]);
    const outlookValidation = validateAccount(selectedAccount?.Outlook, [
      'email',
      'token',
    ]);
    const googleDriveValidation = validateAccount(
      selectedAccount?.GoogleDrive,
      ['email', 'token', 'folderSelection'],
    );
    const gmailValidation = validateAccount(selectedAccount?.Gmail, [
      'email',
      'token',
    ]);

    if (!hasDocutranscribe) {
      Toast({
        message: 'Please Connect DocuTranscribe Node to Import Node.',
        variant: 'warning',
      });
      return;
    }

    if (!actionsConfigure?.Docutranscribe?.value) {
      Toast({
        message: 'Please Select document type from DocuTranscribe',
        variant: 'warning',
      });
      return;
    }
    const validateAndShowError = () => {
      const validations = [
        {
          service: 'OneDrive',
          validation: validateAccount(selectedAccount?.OneDrive, [
            'email',
            'token',
            'driveId',
            'folderId',
          ]),
          isActive: isOneDrive,
        },
        {
          service: 'SharePoint',
          validation: validateAccount(selectedAccount?.SharePoint, [
            'email',
            'token',
            'driveId',
            'folderId',
          ]),
          isActive: isSharePoint,
        },
        {
          service: 'Outlook',
          validation: validateAccount(selectedAccount?.Outlook, [
            'email',
            'token',
          ]),
          isActive: isOutlook,
        },
        {
          service: 'Google Drive',
          validation: validateAccount(selectedAccount?.GoogleDrive, [
            'email',
            'token',
            'folderSelection',
          ]),
          isActive: isGoogleDrive,
        },
        {
          service: 'Gmail',
          validation: validateAccount(selectedAccount?.Gmail, [
            'email',
            'token',
          ]),
          isActive: isGmail,
        },
      ];

      return validations.some(({ service, validation, isActive }) => {
        if (isActive && !validation.isValid) {
          return showMissingFieldError(service, validation.missingFields);
        }
        return false;
      });
    };

    if (validateAndShowError()) {
      return;
    }

    const processFlow = async (uploadFlow) => {
      setStatusLoader({ stage: true, id: flow?.id });
      const newStatus = flow.status === 'Stopped' ? 'Running' : 'Stopped';
      await createToken(flow.id);
      const statusUpdate = await editInvoiceConfigurationFlow(
        flow.id,
        newStatus,
      );
      if (statusUpdate.status === 200) {
        logInfo(
          {
            data: newStatus === 'Running' ? 'Process start' : 'Process stop',
            label: 'success',
          },
          flow?.id,
        );
      }
      await uploadFlow();
      await getAllFlow(filterValue);
      setStatusLoader({ stage: false, id: flow?.id });
    };

    if (
      (isOneDrive && oneDriveValidation.isValid) ||
      (isSharePoint && sharePointValidation.isValid) ||
      (isOutlook && outlookValidation.isValid)
    ) {
      await processFlow(microSoftUploadFlow);
    } else if (
      (isGoogleDrive && googleDriveValidation.isValid) ||
      (isGmail && gmailValidation.isValid)
    ) {
      await processFlow(googleUploadFlow);
    }
  };

  const handleApplyFilter = () => {
    setApplyFilter(true);
    setFilterAnchorEl(null);
  };

  const handleResetFilter = (filterField) => {
    setFilterValue((prev) => ({
      ...prev,
      [filterField]: '',
    }));
    setApplyFilter(true);
    setFilterAnchorEl(null);
  };

  useEffect(() => {
    if (applyFilter) {
      getAllFlow(filterValue);
    }
  }, [applyFilter, filterValue]);

  useEffect(() => {
    if (flows?.length > 0) {
      const runningFlow = flows?.filter((flow) => flow?.status === 'Running');
      if (userId && !isConnected && runningFlow?.length > 0) {
        dispatch(connectSocket(userId));
      } else if (runningFlow?.length === 0 && isConnected) {
        // dispatch(disconnectSocket());
      }
    }
  }, [dispatch, flows, userId, isConnected]);

  useEffect(
    () => () => {
      if (isConnectedRef.current) {
        // dispatch(disconnectSocket());
      }
    },
    [dispatch],
  );

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelectedRows(flows.map((flow) => flow.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleCheckboxClick = (id) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((rowId) => rowId !== id)
        : [...prevSelected, id],
    );
  };
  const clearStatusFilter = () => {
    setFilterValue((prev) => ({
      ...prev,
      status: '',
    }));
  };

  return (
    <Root className={classes.root}>
      {isLoading ? (
        <Box className={classes.loaderBox}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid>
          <Grid container alignItems="center">
            <Grid item xs={9}>
              <Breadcrumbs>
                <Typography variant="h4" color={lightPalette.darkPurple.main}>
                  Flows
                </Typography>
              </Breadcrumbs>
            </Grid>
            <Grid
              item
              xs={3}
              gap={2}
              className={classes.addFlowAndFilterFlowDropdownGrid}
            >
              <Box className={classes.secondGridBox} gap={2}>
                <SelectOption
                  totalData={flows}
                  selectedRow={selectedRows}
                  handleApiCompletion={() => getAllFlow(filterValue)}
                  allRowSelected={selectedRows.length === flows.length}
                  setSelectedRows={setSelectedRows}
                  setIsLoading={setIsLoading}
                />
                <Box>
                  <Box>
                    <Button
                      variant="contained"
                      className={classes.addNewFlowButton}
                      onClick={() => openAddFlowBackdrop({})}
                    >
                      Add Flows
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
          <Grid container marginTop="10px">
            <TableContainer
              className={classes.tableMainContainer}
              component={Paper}
            >
              <Table stickyHeader className={classes.table}>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell
                      className={classes.tableHeaderCell}
                      padding="checkbox"
                    >
                      <Checkbox
                        indeterminate={
                          selectedRows.length > 0 &&
                          selectedRows.length < flows.length
                        }
                        checked={
                          flows.length > 0 &&
                          selectedRows.length === flows.length
                        }
                        onChange={handleSelectAllClick}
                      />
                    </TableCell>
                    <TableCell className={classes.tableHeaderCell}>
                      <Box className={classes.filterCellBoxStyle}>
                        <Typography variant="body1">Name</Typography>
                        <MenuIcon
                          className={globalClasses.cursorPointerClass}
                          onClick={(event) => handleFilterClick(event, 'name')}
                        />
                      </Box>
                    </TableCell>
                    <TableCell className={classes.tableHeaderCell}>
                      Connections
                    </TableCell>
                    <TableCell className={classes.tableHeaderCell}>
                      <Box className={classes.filterCellBoxStyle}>
                        <Typography variant="body1">Status</Typography>
                        <MenuIcon
                          className={globalClasses.cursorPointerClass}
                          onClick={(event) =>
                            handleFilterClick(event, 'status')
                          }
                        />
                      </Box>
                    </TableCell>
                    <TableCell className={classes.tableHeaderCell} />
                  </TableRow>
                </TableHead>
                <TableBody className={classes.tableBody}>
                  {flows?.length > 0 ? (
                    flows.map((flow) => (
                      <TableRow
                        key={flow?.id}
                        onClick={() => handleEditorClick(flow?.id)}
                        className={`${globalClasses.cursorPointerClass} ${classes.tableRowClass}`}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedRows.includes(flow.id)}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => handleCheckboxClick(flow.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1">{flow?.name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box className={classes.stepperBox}>
                            <Stepper
                              connector={<QontoConnector />}
                              className={classes.stepper}
                            >
                              {connectedNodes[flow?.id]?.length ? (
                                connectedNodes[flow?.id]?.map((label) => (
                                  <Step key={label}>
                                    <StepLabel
                                      StepIconComponent={() =>
                                        CustomStepIcon({
                                          icon: label,
                                        })
                                      }
                                    />
                                  </Step>
                                ))
                              ) : (
                                <Typography
                                  variant="body1"
                                  sx={{ color: '#FFA726' }}
                                >
                                  {edgeMessage}
                                </Typography>
                              )}
                            </Stepper>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box
                            className={`${globalClasses.displayFlex} ${globalClasses.displayAlignItem}`}
                          >
                            <CustomTooltip
                              title={
                                flow?.status === 'Running'
                                  ? 'Stop the Running Flow'
                                  : 'Start the Stopped flow'
                              }
                            >
                              <Chip
                                label={
                                  <Box display="flex" alignItems="center">
                                    <Box
                                      component="span"
                                      width={8}
                                      height={8}
                                      borderRadius="50%"
                                      mr={1}
                                      bgcolor={
                                        flow.status === 'Running'
                                          ? lightPalette.jadeGreen.main
                                          : lightPalette.colorRed.main
                                      }
                                    />
                                    <Typography
                                      variant="body1"
                                      color={
                                        flow.status === 'Running'
                                          ? lightPalette.colorGreen.main
                                          : lightPalette.colorRed.main
                                      }
                                    >
                                      {flow?.status}
                                    </Typography>
                                  </Box>
                                }
                                className={`${classes.chipPadding} ${flow?.status === 'Running' ? classes.runningChip : classes.stopChip}`}
                                onClick={(event) =>
                                  handleFlowRunning(event, flow)
                                }
                              />
                            </CustomTooltip>
                            <CircularProgress
                              size={15}
                              className={`${statusLoader?.stage && statusLoader?.id === flow?.id ? globalClasses.visibility : globalClasses.notVisibility} ${classes.statusLoader}`}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Button
                            id="basic-button"
                            aria-controls={open ? 'basic-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? 'true' : undefined}
                            onClick={(e) => {
                              handleClick(e, flow);
                            }}
                          >
                            <MoreVertIcon
                              sx={{ color: lightPalette.darkPurple.main }}
                            />
                          </Button>
                          <Menu
                            id="basic-menu"
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            MenuListProps={{
                              'aria-labelledby': 'basic-button',
                            }}
                            PaperProps={{
                              style: {
                                boxShadow: 'none',
                                border: '1px solid #e0e0e0',
                              },
                            }}
                          >
                            <MenuItem
                              onClick={(event) =>
                                openEditBackdrop(event, currentFlow)
                              }
                            >
                              Edit
                            </MenuItem>
                            <MenuItem
                              onClick={(event) => openDeleteBackdrop(event)}
                            >
                              Delete
                            </MenuItem>
                            <MenuItem
                              onClick={(event) => openLogs(event, currentFlow)}
                            >
                              Logs
                            </MenuItem>
                          </Menu>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography
                          variant="body1"
                          align="center"
                          sx={{ padding: 2 }}
                        >
                          No flows found. Create a new one.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <FilterPopup
              open={filterOpen}
              anchorEl={filterAnchorEl}
              onClose={handleFilterClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
            >
              <Box className={classes.filterPopupRootBox}>
                {filterAnchorElField === 'name' && (
                  <TextField
                    size="small"
                    variant="outlined"
                    fullWidth
                    value={filterValue.name}
                    onChange={handleFilterChange}
                    placeholder="Search here"
                  />
                )}
                {filterAnchorElField === 'status' && (
                  <Autocomplete
                    size="small"
                    options={statusOption}
                    ListboxProps={{ className: 'Autocomplete_scrollbar' }}
                    value={filterValue.status}
                    onChange={handleAutocompleteChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search here"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                    clearIcon={
                      filterValue.status ? (
                        <ClearIcon onClick={clearStatusFilter} />
                      ) : null
                    }
                  />
                )}
                <Box className={classes.filterButtonBoxClass}>
                  <Button
                    variant="outlined"
                    onClick={() => handleResetFilter(filterAnchorElField)}
                    className={classes.filterButtonClass}
                  >
                    {FILTER.RESET_FILTER}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleApplyFilter}
                    className={classes.filterButtonClass}
                  >
                    {FILTER.APPLY_FILTER}
                  </Button>
                </Box>
              </Box>
            </FilterPopup>
          </Grid>
        </Grid>
      )}
      <Backdrop
        className={classes.newFlowBackdrop}
        open={deleteConfigurationFlow}
      >
        {isBackDropLoader && (
          <Backdrop
            className={classes.editFlowLoaderBox}
            open={isBackDropLoader}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
        <Card className={classes.cardComponent}>
          <CardHeader
            className={classes.CardHeader}
            title={
              <Grid container alignItems="center">
                <Grid item xs={10}>
                  <Typography variant="h4">Delete Flow</Typography>
                </Grid>
                <Grid item xs={2} align="right">
                  <Avatar
                    onClick={closeKeyNameForm}
                    src={closeIcon}
                    className={classes.closeAction}
                  />
                </Grid>
              </Grid>
            }
          />

          <CardContent className={classes.CardContent}>
            <Typography>Are you sure you want to delete this?</Typography>
            <Box className={classes.actionButtonBox}>
              <Button
                className={classes.cancelButton}
                onClick={closeKeyNameForm}
              >
                <Typography variant="body1">Cancel</Typography>
              </Button>
              <Button
                variant="contained"
                className={classes.saveButton}
                onClick={() => handleDelete(currentFlow?.id)}
              >
                <Typography variant="body1">Delete</Typography>
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Backdrop>
      {editNewFlow && (
        <AddFlow
          editNewFlow={editNewFlow}
          setEditNewFlow={setEditNewFlow}
          getAllFlow={getAllFlow}
          filterValue={filterValue}
          setCurrentFlow={setCurrentFlow}
          currentFlow={currentFlow}
          setDeleteConfigurationFlow={setDeleteConfigurationFlow}
        />
      )}
    </Root>
  );
};

export default memo(FlowConfiguration);
